import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import notificationService from '../../services/notificationService';
import { useLanguage } from '../../contexts/LanguageContext';
import './NotificationBell.scss';

const NotificationBell = () => {
  const { language } = useLanguage();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState('right');
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    // Initialize with current notifications
    setNotifications(notificationService.getNotifications());
    setUnreadCount(notificationService.getUnreadCount());

    // Subscribe to changes
    const unsubscribe = notificationService.subscribe(({ notifications, unreadCount }) => {
      setNotifications(notifications);
      setUnreadCount(unreadCount);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handleDocumentClick = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        buttonRef.current && !buttonRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleDocumentClick);
    return () => document.removeEventListener('mousedown', handleDocumentClick);
  }, [isOpen]);

  const toggleNotifications = () => {
    setIsOpen(!isOpen);
    
    // Adjust position when opening
    if (!isOpen) {
      setTimeout(() => {
        adjustDropdownPosition();
      }, 10);
    }
  };

  const adjustDropdownPosition = () => {
    if (dropdownRef.current && buttonRef.current) {
      const dropdown = dropdownRef.current;
      const button = buttonRef.current;
      const rect = button.getBoundingClientRect();
      const dropdownRect = dropdown.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      
      // Check if dropdown goes off screen to the right
      if (rect.right + dropdownRect.width > viewportWidth - 20) {
        setDropdownPosition('left');
      } else {
        setDropdownPosition('right');
      }
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      if (notification.read) {
        await notificationService.markAsUnread(notification.id);
      } else {
        await notificationService.markAsRead(notification.id);
      }
    } catch (error) {
      console.error('Error updating notification:', error);
    }
  };

  const handleRemoveNotification = async (e, notificationId) => {
    e.stopPropagation();
    try {
      await notificationService.removeNotification(notificationId);
    } catch (error) {
      console.error('Error removing notification:', error);
      // Notification service handles local removal even if backend fails
    }
  };

  const handleClearAll = async () => {
    try {
      await notificationService.clearAll();
    } catch (error) {
      console.error('Error clearing all notifications:', error);
      // Notification service handles local clearing even if backend fails
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return language === 'en' ? 'Now' : 'Acum';
    } else if (diffInHours < 24) {
      return language === 'en' ? `${diffInHours}h ago` : `${diffInHours}h în urmă`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return language === 'en' ? `${diffInDays}d ago` : `${diffInDays}d în urmă`;
    }
  };

  return (
    <div className="notification-bell-container">
      <button 
        ref={buttonRef}
        className="notification-bell-button"
        onClick={toggleNotifications}
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
          <path d="M4 2C2.8 3.7 2 5.7 2 8" />
          <path d="M22 8c0-2.3-.8-4.3-2-6" />
        </svg>
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div 
          ref={dropdownRef}
          className={`notification-dropdown ${dropdownPosition === 'left' ? 'adjust-left' : ''}`}
        >
          <div className="notification-header">
            <h3>Notificări</h3>
            {notifications.length > 0 && (
              <button 
                className="clear-all-btn"
                onClick={handleClearAll}
              >
                Șterge toate
              </button>
            )}
          </div>

          <div className="notification-list">
            {notifications.length === 0 ? (
              <div className="no-notifications">
                <p>Nu există notificări</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`notification-item ${notification.read ? 'read' : 'unread'}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  {!notification.read && <div className="unread-dot"></div>}
                  
                  <div className="notification-content">
                    <div className="notification-title">
                      {notification.title}
                    </div>
                    <div className="notification-message">
                      {notification.message}
                    </div>
                    <div className="notification-timestamp">
                      {formatTimestamp(notification.timestamp)}
                    </div>
                  </div>
                  
                  <button
                    className="remove-notification-btn"
                    onClick={(e) => handleRemoveNotification(e, notification.id)}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Click-outside is handled via document mousedown listener */}
    </div>
  );
};

export default NotificationBell;
